import { useCollection } from "@/features/collections/useCollection";
import {
  CollectionToolbar,
  CollectionPagination,
} from "@/features/collections/CollectionControls";
import { useClubGuestLinks } from "@/features/guest-links/useGuestLinks";
import {
  GuestLinksSection,
  expiryLabel,
} from "@/features/guest-links/GuestLinksSection";
import { CoachModal } from "@/features/coach/components/CoachModal";
import { Button } from "@/components/ui/Button";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Copy, KeyRound, LayoutGrid, Table2 } from "lucide-react";
import {
  ApiError,
  getAdminTeams,
  getTeamStreamKeyAudit,
  rotateTeamStreamKey,
} from "@/lib/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useToast } from "@/components/ui/toast";
import { unscopedKey } from "@/lib/queryClient";
import type { AdminTeamRow, ClubLiveStatusPayload } from "@/lib/types";
import { useClubLiveConfig, useClubLiveStatus } from "../hooks/useAdminData";
import { AdminIconButton } from "@/components/console/AdminIconButton";
import { AdminPageHeader } from "@/components/console/AdminPageHeader";
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeadCell,
  DataTableHeaderRow,
  DataTableRow,
  DataTableShell,
} from "@/components/ui/DataTable";

type StreamView = "cards" | "table";

const STREAM_VIEW_STORAGE_KEY = "tktic:admin-live-stream-view";

function initialStreamView(): StreamView {
  if (typeof window === "undefined") return "cards";
  return window.sessionStorage.getItem(STREAM_VIEW_STORAGE_KEY) === "table"
    ? "table"
    : "cards";
}

/**
 * `/admin/live` — the club's live page (admin-console-split PR 5).
 *
 * Slim and club-scoped: whether live is on for the platform, the RTMP base
 * a coach points their encoder at, and one row per team — on-air state,
 * viewers watching right now, publish path, and stream-key rotation.
 *
 * Platform live INFRASTRUCTURE (master toggle, MediaMTX diagnostics,
 * cross-club viewer kill / IP blocks, the singleton key) lives on the
 * super-admin console at /super-admin/live — none of it renders here.
 * Viewer counts only include sessions attributed to this club's teams.
 */
export function AdminLivePage() {
  const [streamView, setStreamView] = useState<StreamView>(initialStreamView);
  const [visibilityFilter, setVisibilityFilter] = useState("");
  const [guestTeam, setGuestTeam] = useState<{
    team: AdminTeamRow;
    linkId?: string;
  } | null>(null);
  const statusQuery = useClubLiveStatus({ refetchIntervalMs: 10_000 });
  const configQuery = useClubLiveConfig();
  const teamsQuery = useQuery({
    queryKey: unscopedKey("admin-teams-live"),
    queryFn: getAdminTeams,
    staleTime: 60_000,
  });

  const shares = useClubGuestLinks(teamsQuery.data ?? []);
  const guestLinks = useCollection(shares.links, {
    scope: "club-guest-links",
    text: (link) => `${link.label ?? "Team viewing"} ${link.teamName}`,
    matches: (link, status) =>
      status === "active"
        ? link.active
        : status === "revoked"
          ? !!link.revoked_at
          : !link.active && !link.revoked_at,
  });
  const statusBySlug = new Map(
    (statusQuery.data?.teams ?? []).map((t) => [t.slug, t]),
  );
  const streams = useCollection(teamsQuery.data ?? [], {
    scope: "club-streams",
    initialPageSize: 10,
    text: (t) => t.name,
    matches: (t, f) =>
      f === "live"
        ? !!statusBySlug.get(t.slug)?.active
        : !statusBySlug.get(t.slug)?.active,
    predicate: (team) =>
      !visibilityFilter ||
      (visibilityFilter === "public" ? !!team.public : !team.public),
  });
  const liveNow = (statusQuery.data?.teams ?? []).filter(
    (t) => t.active,
  ).length;
  const viewersNow = (statusQuery.data?.teams ?? []).reduce(
    (sum, t) => sum + t.viewers,
    0,
  );
  const selectStreamView = (view: StreamView) => {
    setStreamView(view);
    window.sessionStorage.setItem(STREAM_VIEW_STORAGE_KEY, view);
  };

  return (
    <div data-testid="admin-live" className="flex flex-col gap-3">
      <AdminPageHeader
        eyebrow="Media"
        title="Live"
        description="Matchday streams and permitted guest viewing."
        meta={
          statusQuery.isFetching ? "Refreshing..." : "Auto-refreshes every 10 s"
        }
      />

      <ClubLiveConfigCard
        config={configQuery.data}
        loading={configQuery.isLoading}
        liveNow={liveNow}
        viewersNow={viewersNow}
      />

      <section data-testid="admin-live-teams" className="club-streams">
        <header className="club-live-list-head">
          <div>
            <p className="eyebrow">Squads</p>
            <h2>Squad streams</h2>
            <p className="text-sm text-ink-muted">
              Use cards for a visual status check or switch to the table to
              scan a larger club.
            </p>
          </div>
          <div
            className="club-live-view-switch"
            role="group"
            aria-label="Squad stream view"
          >
            <button
              type="button"
              aria-pressed={streamView === "cards"}
              onClick={() => selectStreamView("cards")}
            >
              <LayoutGrid aria-hidden />
              Cards
            </button>
            <button
              type="button"
              aria-pressed={streamView === "table"}
              onClick={() => selectStreamView("table")}
            >
              <Table2 aria-hidden />
              Table
            </button>
          </div>
        </header>
        {streams.controls ? (
          <div className="club-live-filters">
            <CollectionToolbar
              collection={streams}
              label="squad streams"
              filterLabel="Broadcast"
              searchPlaceholder="Squad name"
              filters={[
                { value: "", label: "All" },
                { value: "live", label: "Live" },
                { value: "offline", label: "Offline" },
              ]}
              extraFiltersAfter
              extraFilters={[
                {
                  label: "Visibility",
                  ariaLabel: "Filter squad streams by visibility",
                  value: visibilityFilter,
                  clearValue: "",
                  options: [
                    { value: "", label: "All" },
                    { value: "public", label: "Public" },
                    { value: "private", label: "Private" },
                  ],
                  onChange: (value) => {
                    setVisibilityFilter(value);
                    streams.setPage(1);
                  },
                },
              ]}
            />
          </div>
        ) : null}
        {teamsQuery.isLoading ? (
          <p className="text-ink-muted text-xs font-mono">Loading teams…</p>
        ) : teamsQuery.isError ? (
          <p className="text-destructive text-xs font-mono">
            Couldn’t load teams.
          </p>
        ) : (teamsQuery.data ?? []).length === 0 ? (
          <p className="text-ink-muted text-xs font-mono">
            No teams yet — create one in Teams.
          </p>
        ) : (
          <SquadStreams
            view={streamView}
            teams={streams.items}
            statusBySlug={statusBySlug}
            links={shares.links}
            linksPending={shares.loading || !!shares.error}
            onGuestLinks={(team) => setGuestTeam({ team })}
          />
        )}
        <CollectionPagination collection={streams} />
      </section>
      <section className="club-guest-history rounded-panel border border-rule bg-surface p-5">
        <p className="eyebrow">Permitted guest viewing</p>
        <h2>Guest links</h2>
        <p className="text-sm text-ink-muted">
          Guest access covers the selected squad’s live streams and match
          recordings.
        </p>
        {shares.loading ? <p role="status">Loading guest links…</p> : null}
        {shares.error ? (
          <p role="alert">
            Some team links could not be loaded.{" "}
            <Button variant="ghost" onClick={() => void shares.retry()}>
              Retry
            </Button>
          </p>
        ) : null}
        <CollectionToolbar
          collection={guestLinks}
          label="club guest links"
          filters={[
            { value: "", label: "All statuses" },
            { value: "active", label: "Active" },
            { value: "expired", label: "Expired" },
            { value: "revoked", label: "Revoked" },
          ]}
        />
        <ul>
          {guestLinks.items.map((link) => (
            <li key={link.id} className="club-guest-row">
              <div>
                <h3>{link.label ?? "Team viewing"}</h3>
                <p className="text-sm text-ink-muted">
                  {link.teamName} · {expiryLabel(link.expires_at)}
                </p>
              </div>
              <span className="club-guest-status" data-active={link.active}>
                {link.revoked_at
                  ? "Revoked"
                  : link.active
                    ? "Active"
                    : "Expired"}
              </span>
              <Button
                variant="ghost"
                onClick={() => {
                  const team = teamsQuery.data?.find(
                    (t) => t.id === link.team_id,
                  );
                  if (team) setGuestTeam({ team, linkId: link.id });
                }}
              >
                {link.active ? "Manage" : "View"}
              </Button>
            </li>
          ))}
        </ul>
        {!shares.loading && !shares.error && !guestLinks.items.length ? (
          <p className="mt-4 text-sm text-ink-muted">
            {shares.links.length ? "No matching links." : "No guest links yet."}
          </p>
        ) : null}
        <CollectionPagination collection={guestLinks} />
      </section>
      {guestTeam ? (
        <CoachModal
          title={
            guestTeam.linkId
              ? "Guest viewing link"
              : "Create a guest viewing link"
          }
          onClose={() => setGuestTeam(null)}
        >
          <GuestLinksSection
            teamId={guestTeam.team.id}
            initialExpiry="date"
            mode={guestTeam.linkId ? "manage" : "create"}
            linkId={guestTeam.linkId}
            presentation="admin"
            teamName={guestTeam.team.name}
            onRequestClose={() => setGuestTeam(null)}
          />
        </CoachModal>
      ) : null}
    </div>
  );
}

/**
 * The live settings a club can SEE (never edit, never a key): whether the
 * platform has live enabled, the RTMP base for encoders, and the offline
 * message viewers get between streams. Enable/disable is a platform
 * decision made by the Tktic operator.
 */
function ClubLiveConfigCard({
  config,
  loading,
  liveNow,
  viewersNow,
}: {
  config: ReturnType<typeof useClubLiveConfig>["data"];
  loading: boolean;
  liveNow: number;
  viewersNow: number;
}) {
  const toast = useToast();
  const enabled = config?.enabled ?? false;

  return (
    <section
      data-testid="club-live-config"
      data-live-enabled={enabled ? "true" : "false"}
      className="club-live-config rounded-panel border border-rule bg-elevated p-5"
    >
      <p className="eyebrow">Club live service</p>
      <h2>Ready for matchday</h2>
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={
            enabled
              ? "rounded-pill border border-good/40 bg-good/10 px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider text-good"
              : "rounded-pill border border-rule bg-elevated px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider text-ink-muted"
          }
        >
          {loading ? "Checking…" : enabled ? "Live enabled" : "Live paused"}
        </span>
        <span className="font-mono text-[0.65rem] text-ink-muted">
          {liveNow} on air · {viewersNow} watching
        </span>
        <span className="ml-auto text-[0.6rem] text-ink-muted">
          Live streaming is switched on and off by the Tktic operator.
        </span>
      </div>
      <div className="mt-3 grid gap-2 md:grid-cols-2">
        <div className="rounded-inset border border-rule/60 bg-elevated px-2 py-1.5">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[0.55rem] font-display uppercase tracking-wider text-ink-muted">
              RTMP server (paste into the encoder)
            </span>
            {config?.rtmp_public_url ? (
              <AdminIconButton
                icon={Copy}
                label="Copy RTMP server URL"
                size="sm"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(config.rtmp_public_url);
                    toast.success("RTMP server URL copied");
                  } catch {
                    toast.error("Couldn't copy the URL");
                  }
                }}
              />
            ) : null}
          </div>
          <p className="break-all font-mono text-xs text-ink">
            {config?.rtmp_public_url || "Not configured yet"}
          </p>
          <p className="mt-1 text-[0.6rem] text-ink-muted">
            Each team's Stream Key below completes the pair.
          </p>
        </div>
        <div className="rounded-inset border border-rule/60 bg-elevated px-2 py-1.5">
          <span className="text-[0.55rem] font-display uppercase tracking-wider text-ink-muted">
            Offline message viewers see
          </span>
          <p className="font-mono text-xs text-ink">
            {config?.offline_message || "—"}
          </p>
        </div>
      </div>
    </section>
  );
}

type TeamLiveStatus = ClubLiveStatusPayload["teams"][number];

function SquadStreams({
  view,
  teams,
  statusBySlug,
  links,
  linksPending,
  onGuestLinks,
}: {
  view: StreamView;
  teams: AdminTeamRow[];
  statusBySlug: Map<string, TeamLiveStatus>;
  links: ReturnType<typeof useClubGuestLinks>["links"];
  linksPending: boolean;
  onGuestLinks: (team: AdminTeamRow) => void;
}) {
  const activeLinksFor = (teamId: string) =>
    linksPending
      ? null
      : links.filter((link) => link.team_id === teamId && link.active).length;

  if (view === "cards") {
    return (
      <ul role="list" className="club-live-grid">
        {teams.map((team) => (
          <li key={team.id}>
            <ClubTeamLiveRow
              view="cards"
              team={team}
              status={statusBySlug.get(team.slug)}
              onGuestLinks={() => onGuestLinks(team)}
              activeLinks={activeLinksFor(team.id)}
            />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <DataTableShell className="club-live-table-shell">
      <DataTable className="club-live-table">
        <caption className="sr-only">
          Squad live stream status and controls
        </caption>
        <DataTableHead>
          <DataTableHeaderRow>
            <DataTableHeadCell scope="col">Squad</DataTableHeadCell>
            <DataTableHeadCell scope="col">Broadcast</DataTableHeadCell>
            <DataTableHeadCell scope="col">Viewers</DataTableHeadCell>
            <DataTableHeadCell scope="col">Guest links</DataTableHeadCell>
            <DataTableHeadCell scope="col">Visibility</DataTableHeadCell>
            <DataTableHeadCell scope="col">
              <span className="sr-only">Actions</span>
            </DataTableHeadCell>
          </DataTableHeaderRow>
        </DataTableHead>
        <DataTableBody>
          {teams.map((team) => (
            <ClubTeamLiveRow
              key={team.id}
              view="table"
              team={team}
              status={statusBySlug.get(team.slug)}
              onGuestLinks={() => onGuestLinks(team)}
              activeLinks={activeLinksFor(team.id)}
            />
          ))}
        </DataTableBody>
      </DataTable>
    </DataTableShell>
  );
}

function ClubTeamLiveRow({
  view,
  team,
  status,
  onGuestLinks,
  activeLinks,
}: {
  view: StreamView;
  team: AdminTeamRow;
  status: TeamLiveStatus | undefined;
  onGuestLinks: () => void;
  activeLinks: number | null;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  // Composite paste-ready Stream Key (``<slug>/<key>``) — the value the
  // admin pastes into OBS verbatim (the documented RTMP Server is the base
  // URL alone, so the bare key would not work on its own).
  const [rotated, setRotated] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Per-row audit lookup so the confirm dialog can warn the admin
  // specifically — "you'd be locking out <coach> who rotated 2 hours ago".
  // Gated on `open` so the list doesn't fan out N audit fetches on mount.
  const auditQuery = useQuery({
    queryKey: unscopedKey("team-stream-key-audit", { teamId: team.id }),
    queryFn: () => getTeamStreamKeyAudit(team.id),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    enabled: open,
  });

  const onRotate = async () => {
    setPending(true);
    setError(null);
    try {
      const resp = await rotateTeamStreamKey(team.id);
      // Prefer ``resp.stream_path`` so we stay in sync with the server's
      // canonical publish path; strip the leading ``live/`` namespace so
      // the value is what an admin pastes verbatim into OBS.
      const composite = resp.stream_path.replace(/^live\//, "");
      setRotated(composite);
      await queryClient.invalidateQueries({
        queryKey: unscopedKey("admin-teams-live"),
      });
      queryClient.setQueryData(
        unscopedKey("team-stream-key-audit", { teamId: team.id }),
        {
          last_rotated_at: resp.last_rotated_at,
          last_rotated_by: resp.last_rotated_by,
        },
      );
      setOpen(false);
      toast.success("Stream key rotated");
    } catch (err) {
      const detail =
        err instanceof ApiError && typeof err.detail === "string"
          ? err.detail
          : "Couldn't rotate the team stream key.";
      setError(detail);
      toast.error("Couldn't rotate stream key", { description: detail });
    } finally {
      setPending(false);
    }
  };

  const confirmation = open ? (
    <ConfirmDialog
      title="Rotate stream key?"
      message={
        <div className="flex flex-col gap-2">
          <p>
            The previous encoder credential for {team.name} will stop working.
            Rotating generates a new key and{" "}
            <strong>immediately disconnects</strong> any encoder still streaming
            with the previous key. The new key is shown <strong>once</strong> in
            this dialog — copy it before closing.
          </p>
          {auditQuery.data?.last_rotated_by ? (
            <p className="rounded-inset border border-destructive/40 bg-destructive/10 p-2 text-xs">
              <strong>{auditQuery.data.last_rotated_by}</strong> rotated this key
              at <code>{auditQuery.data.last_rotated_at}</code>. If they&rsquo;re
              still using it, rotating now will lock them out — confirm with the
              team admin before proceeding.
            </p>
          ) : (
            <p className="text-xs text-ink-muted">
              This team&rsquo;s key has not been rotated before — no audit trail
              to check.
            </p>
          )}
        </div>
      }
      confirmLabel={pending ? "Rotating…" : "Rotate key"}
      danger
      onConfirm={() => void onRotate()}
      onClose={() => {
        if (pending) return;
        setOpen(false);
        setError(null);
      }}
      data-testid="admin-live-team-rotate-confirm"
    />
  ) : null;

  if (view === "table") {
    return (
      <>
        <DataTableRow
          data-testid="admin-live-team-row"
          data-team-slug={team.slug}
        >
          <DataTableCell data-label="Squad">
            <h3>{team.name}</h3>
            <span className="text-ink-muted">{team.game_format}</span>
            <span className="sr-only">live/{team.slug}</span>
            {rotated ? (
              <p
                data-testid="admin-live-team-rotated"
                className="club-live-key-result"
              >
                New Stream Key: <code>{rotated}</code>
              </p>
            ) : null}
            {error ? <p className="club-live-key-error">{error}</p> : null}
          </DataTableCell>
          <DataTableCell data-label="Broadcast">
            <div className="club-live-table-status">
              <span className="club-live-dot" data-live={!!status?.active} />
              <LiveStatePill status={status} />
              {!status ? <span className="text-ink-muted">Checking…</span> : null}
            </div>
          </DataTableCell>
          <DataTableCell data-label="Viewers">
            <span
              data-testid="admin-live-team-viewers"
              className="club-live-table-number"
            >
              {status ? status.viewers : "—"}
            </span>
          </DataTableCell>
          <DataTableCell data-label="Guest links">
            <strong>
              {activeLinks === null ? "—" : `${activeLinks} active`}
            </strong>
            <Button variant="ghost" onClick={onGuestLinks}>
              Create link
            </Button>
          </DataTableCell>
          <DataTableCell data-label="Visibility">
            <span className="club-live-visibility" data-public={!!team.public}>
              {team.public ? "Public" : "Private"}
            </span>
          </DataTableCell>
          <DataTableCell data-label="Actions">
            <div className="club-live-table-actions">
              <Link
                className="club-live-viewer"
                to="/teams/$teamSlug/live"
                params={{ teamSlug: team.slug }}
              >
                Open viewer
              </Link>
              <Button
                variant="ghost"
                className="club-live-rotate"
                onClick={() => setOpen(true)}
                data-testid="admin-live-team-rotate"
              >
                Rotate key
              </Button>
            </div>
          </DataTableCell>
        </DataTableRow>
        {confirmation}
      </>
    );
  }

  return (
    <>
    <article
      data-testid="admin-live-team-row"
      data-team-slug={team.slug}
      className="club-live-card rounded-panel border border-rule bg-surface"
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-display text-sm text-ink truncate">{team.name}</p>
          <LiveStatePill status={status} />
          {status && status.viewers > 0 ? (
            <span
              data-testid="admin-live-team-viewers"
              className="font-mono text-[0.65rem] text-ink"
            >
              {status.viewers} watching
            </span>
          ) : null}
        </div>
        <p className="font-mono text-[0.6rem] uppercase tracking-mono text-ink-muted">
          live/{team.slug}
          {team.public ? " · public" : " · private"}
          {team.has_stream_key ? " · keyed" : " · no key"}
        </p>
        {rotated ? (
          <p
            data-testid="admin-live-team-rotated"
            className="font-mono text-[0.65rem] text-ink"
          >
            New Stream Key (paste into OBS):{" "}
            <code className="rounded-inset bg-canvas px-1 py-0.5 text-ink">
              {rotated}
            </code>
          </p>
        ) : null}
        {error ? (
          <p className="font-mono text-[0.65rem] text-destructive">{error}</p>
        ) : null}
      </div>
      <div className="club-live-preview">
        <div className="club-live-status">
          <span className="club-live-dot" data-live={!!status?.active} />
          {status
            ? status.active
              ? "Live broadcast"
              : "Between broadcasts"
            : "Checking stream…"}
        </div>
        <div>
          <strong>{status ? status.viewers : "—"}</strong> viewers
        </div>
      </div>
      <p className="text-sm text-ink-muted">
        {status?.active
          ? "Authorized viewers can watch the current team stream."
          : "Viewers see the offline message until the team broadcasts."}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Link
          className="club-live-viewer"
          to="/teams/$teamSlug/live"
          params={{ teamSlug: team.slug }}
        >
          Open viewer
        </Link>
        <Button
          variant="ghost"
          className="club-live-rotate"
          onClick={() => setOpen(true)}
          data-testid="admin-live-team-rotate"
        >
          <KeyRound aria-hidden />
          Rotate stream key
        </Button>
      </div>
      <div className="club-live-guest">
        <div>
          <h3>Guest links</h3>
          <p className="text-sm text-ink-muted">
            {activeLinks === null
              ? "Checking guest links…"
              : `${activeLinks} active · manage in the list below`}
          </p>
        </div>
        <Button
          variant="ghost"
          aria-label={`Create guest viewing link for ${team.name}`}
          onClick={onGuestLinks}
        >
          Create link
        </Button>
      </div>
      {confirmation}
    </article>
    </>
  );
}

function LiveStatePill({
  status,
}: {
  status: ClubLiveStatusPayload["teams"][number] | undefined;
}) {
  if (!status) return null;
  if (status.active) {
    return (
      <span className="rounded-pill border border-good/40 bg-good/10 px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider text-good">
        On air
      </span>
    );
  }
  if (status.ready) {
    return (
      <span className="rounded-pill border border-accent/40 bg-accent/10 px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider text-accent">
        Ready
      </span>
    );
  }
  return (
    <span className="rounded-pill border border-rule bg-field px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider text-ink-muted">
      Offline
    </span>
  );
}
